/* Tierly Price Ticker — storefront renderer.
   Reads the JSON data island emitted by price-ticker.liquid (the `tierly.widget`
   metafield plus a few product fields) and draws a step "drop price" chart.
   No network calls: the price only moves when an order lands, and that is
   exactly when Tierly rewrites the metafield. */
(function () {
  "use strict";
  var SVGNS = "http://www.w3.org/2000/svg";

  function cssVar(cs, name, fallback) {
    var v = cs.getPropertyValue(name);
    return v && v.trim() ? v.trim() : fallback;
  }

  // A few evenly-spaced, human-rounded gridline values (1/2/5 × 10ⁿ) across a
  // range — so the chart shows ~4 clean price levels, never one label per tier.
  function niceTicks(min, max, count) {
    var span = Math.max(max - min, 1);
    var step0 = span / Math.max(count, 1);
    var mag = Math.pow(10, Math.floor(Math.log(step0) / Math.LN10));
    var norm = step0 / mag;
    var step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
    var out = [];
    for (var t = Math.ceil(min / step) * step; t <= max + 1e-6; t += step) out.push(t);
    return out;
  }

  function fmtMoney(cents, currency) {
    var dollars = cents / 100;
    var hasFraction = Math.round(cents) % 100 !== 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0,
      }).format(dollars);
    } catch (e) {
      return "$" + dollars.toLocaleString("en-US");
    }
  }

  var SKELETON =
    '<div class="tx-head">' +
      '<div class="tx-id">' +
        '<div class="tx-sym"><span class="tx-live-dot"></span><span class="js-sym"></span></div>' +
        '<h1 class="js-name"></h1>' +
        '<div class="tx-item js-item"></div>' +
      "</div>" +
      '<div class="tx-price">' +
        '<div class="tx-now js-now"></div>' +
        '<div class="tx-chg js-chg"></div>' +
        '<div class="tx-sub">since drop open</div>' +
      "</div>" +
    "</div>" +
    '<div class="tx-chart-wrap js-chart"><div class="tx-tip js-tip"></div></div>' +
    '<div class="tx-next">' +
      '<div class="tx-next-top"><span class="tx-warn js-next-label"></span><span class="tx-count js-next-count"></span></div>' +
      '<div class="tx-bar"><div class="tx-bar-fill js-bar"></div></div>' +
    "</div>" +
    '<div class="tx-strip js-strip"></div>' +
    '<div class="tx-note js-note"></div>';

  function stat(k, v) {
    return '<div class="tx-stat"><div class="k">' + k + '</div><div class="v">' + v + "</div></div>";
  }

  function renderOne(root) {
    if (root.dataset.tierlyDone) return;
    var dataEl = root.querySelector(".trax-ticker-data");
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var w = data && data.w;
    if (!w || !w.enabled) return;
    root.dataset.tierlyDone = "1";

    var currency = data.currency || "USD";
    var money = function (cents) { return fmtMoney(cents, currency); };
    var reduce = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Palette from the widget's CSS vars — one source of truth for card + chart.
    var cs = getComputedStyle(root);
    var C = {
      gold:   cssVar(cs, "--gold", "#B8891C"),
      goldHi: cssVar(cs, "--gold-hi", "#946409"),
      amber:  cssVar(cs, "--amber", "#E0B94E"),
      ink:    cssVar(cs, "--ink", "#1A1A1A"),
      muted:  cssVar(cs, "--muted", "#6F6A60"),
      grid:   cssVar(cs, "--grid", "#EFEBE4"),
      bg:     cssVar(cs, "--bg", "#FFFFFF"),
    };

    // ── Data (cents in the metafield; dollars for chart geometry) ──
    var tiers = (w.tiers || []).map(function (t) {
      return { priceC: t.price_cents, price: t.price_cents / 100, units: t.units };
    });
    var current = {
      priceC: w.current_price_cents,
      price: w.current_price_cents / 100,
      units: w.units_into_tier || 0,
      capacity: w.capacity || w.tier_size || 0,
    };
    var mode = w.mode === "display" ? "display" : "auto";
    var atCeiling = !!w.at_ceiling;
    // Display-only stories never re-price, so suppress all forward-looking claims.
    var nextPriceC = mode === "display" || atCeiling || w.next_price_cents == null ? null : w.next_price_cents;
    var openC = w.open_price_cents != null ? w.open_price_cents : (tiers[0] ? tiers[0].priceC : w.current_price_cents);
    var highC = w.high_price_cents != null ? w.high_price_cents : w.current_price_cents;
    var open = openC / 100;
    var soldCount = w.sold_count || 0;

    var segments = tiers.concat([{ price: current.price, units: current.units, live: true }]);

    // ── Skeleton + header/text ──
    var box = document.createElement("div");
    box.innerHTML = SKELETON;
    root.appendChild(box);
    var q = function (sel) { return box.querySelector(sel); };

    q(".js-sym").textContent = (w.symbol || "LIVE") + " · LIVE";
    // Drop a trailing item number (e.g. "…Pamp Gator 71733" -> "…Pamp Gator").
    q(".js-name").textContent = (data.productName || "").replace(/\s+#?\d{4,}\s*$/, "");
    q(".js-item").textContent = "LIMITED QUANTITY DROP";
    q(".js-now").textContent = money(current.priceC);
    var chgPct = open > 0 ? ((current.price - open) / open * 100).toFixed(1) : "0.0";
    q(".js-chg").textContent = money(current.priceC - openC) + "  (+" + chgPct + "%)";

    // Next-tier bar. Display-only: a truthful "founding → now" summary with no
    // forward price claim. Auto: the live "N left → next price" scarcity bar.
    var barFill = q(".js-bar");
    var barPct;
    if (mode === "display") {
      var gainPct = openC > 0 ? Math.round((current.priceC - openC) / openC * 100) : 0;
      q(".js-next-label").textContent = "FOUNDING " + money(openC);
      q(".js-next-count").textContent =
        "NOW " + money(current.priceC) + "  ·  +" + money(current.priceC - openC) + " (+" + gainPct + "%)";
      barPct = 100;
    } else {
      var remaining = Math.max(current.capacity - current.units, 0);
      if (nextPriceC != null) {
        q(".js-next-label").textContent = "NEXT PRICE " + money(nextPriceC);
        q(".js-next-count").textContent = remaining + " OF " + current.capacity + " LEFT AT " + money(current.priceC);
      } else {
        q(".js-next-label").textContent = "FINAL TIER";
        q(".js-next-count").textContent = remaining + " LEFT — DROP CLOSES AT SELLOUT";
      }
      barPct = current.capacity > 0 ? Math.min(current.units / current.capacity * 100, 100) : 100;
    }
    if (reduce) barFill.style.width = barPct + "%";
    else requestAnimationFrame(function () { barFill.style.width = barPct + "%"; });

    // Stats strip
    var strip = q(".js-strip");
    if (data.showStats === false) {
      strip.remove();
    } else {
      strip.innerHTML =
        stat("Units Sold", soldCount) +
        stat("Opening Price", money(openC)) +
        stat("All-Time High", money(highC));
    }

    // Note
    var note = q(".js-note");
    if (data.showNote === false) {
      note.remove();
    } else if (mode === "display") {
      note.innerHTML =
        "This is a <b>founding-price</b> drop — early buyers pay the least. The " +
        "price only moves up, so the price you see now is the lowest it will ever be.";
    } else {
      note.innerHTML =
        "This price only moves <b>up</b>. Every unit sold pushes it closer to the " +
        "next tier — the price you see now is the lowest it will ever be again.";
    }

    drawChart(q(".js-chart"), q(".js-tip"), {
      segments: segments, tiers: tiers, current: current, open: open,
      openC: openC, highC: highC, nextPriceC: nextPriceC, dropStartLabel: w.drop_start_label || "",
      money: money, reduce: reduce, C: C,
    });
  }

  function drawChart(wrap, tip, m) {
    var segments = m.segments, current = m.current, money = m.money, C = m.C;

    // cumulative step points
    var pts = [], cum = 0;
    segments.forEach(function (s) {
      pts.push({ x: cum, price: s.price });
      cum += s.units;
      pts.push({ x: cum, price: s.price });
    });
    var totalUnits = cum;

    var W = 680, H = 300, padL = 14, padR = 62, padT = 26, padB = 34;
    var priceVals = segments.map(function (s) { return s.price; });
    var nextP = m.nextPriceC != null ? m.nextPriceC / 100 : null;
    var high = m.highC / 100;
    var minP = Math.min.apply(null, priceVals.concat([m.open]));
    var maxP = Math.max.apply(null, priceVals.concat([nextP != null ? nextP : high, high]));
    var range = Math.max(maxP - minP, 1);
    var yMin = Math.max(0, minP - range * 0.18);
    var yMax = maxP + range * 0.10;
    var xMax = Math.max(totalUnits, 1) * 1.04;
    var X = function (u) { return padL + (u / xMax) * (W - padL - padR); };
    var Y = function (p) { return padT + (1 - (p - yMin) / (yMax - yMin)) * (H - padT - padB); };

    var svg = document.createElementNS(SVGNS, "svg");
    svg.setAttribute("viewBox", "0 0 " + W + " " + H);
    svg.setAttribute("aria-label",
      "Price history: " + money(m.openC) + " to " + money(current.priceC) + " across " + totalUnits + " units sold");
    svg.innerHTML =
      '<defs><linearGradient id="txArea" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="' + C.amber + '" stop-opacity=".28"/>' +
      '<stop offset=".7" stop-color="' + C.amber + '" stop-opacity=".06"/>' +
      '<stop offset="1" stop-color="' + C.amber + '" stop-opacity="0"/></linearGradient></defs>';

    var MONO = "SF Mono, ui-monospace, Menlo, monospace";
    function line(x1, y1, x2, y2, stroke, dash, opacity) {
      var g = document.createElementNS(SVGNS, "line");
      g.setAttribute("x1", x1); g.setAttribute("y1", y1);
      g.setAttribute("x2", x2); g.setAttribute("y2", y2);
      g.setAttribute("stroke", stroke);
      if (dash) g.setAttribute("stroke-dasharray", dash);
      if (opacity != null) g.setAttribute("stroke-opacity", opacity);
      return g;
    }
    function text(x, y, fill, str, anchor) {
      var t = document.createElementNS(SVGNS, "text");
      t.setAttribute("x", x); t.setAttribute("y", y);
      t.setAttribute("font-size", "10"); t.setAttribute("font-family", MONO);
      t.setAttribute("fill", fill);
      if (anchor) t.setAttribute("text-anchor", anchor);
      t.textContent = str;
      return t;
    }

    // Recessive gridlines at a few rounded price levels — never one label per
    // tier (which turned into an unreadable smear at 30+ tiers).
    var gap = (yMax - yMin) * 0.05; // don't draw a tick that collides with next-price
    niceTicks(yMin, yMax, 4).forEach(function (p) {
      if (p < yMin || p > yMax) return;
      if (nextP != null && Math.abs(p - nextP) < gap) return;
      var y = Y(p);
      svg.appendChild(line(padL, y, W - padR, y, C.grid, null, 1));
      svg.appendChild(text(W - padR + 8, y + 3.5, C.muted, money(Math.round(p * 100))));
    });
    // Next-price target — the single forward-looking direct label.
    if (nextP != null) {
      var yn = Y(nextP);
      svg.appendChild(line(padL, yn, W - padR, yn, C.gold, "5 4", ".6"));
      svg.appendChild(text(W - padR + 8, yn + 3.5, C.goldHi, money(m.nextPriceC)));
    }

    // Step path + area
    var d = "", area = "M " + X(0) + " " + Y(yMin) + " ";
    pts.forEach(function (p, i) {
      var px = X(p.x), py = Y(p.price);
      d += (i === 0 ? "M " : "L ") + px.toFixed(1) + " " + py.toFixed(1) + " ";
      area += "L " + px.toFixed(1) + " " + py.toFixed(1) + " ";
    });
    area += "L " + X(totalUnits).toFixed(1) + " " + Y(yMin) + " Z";

    var areaEl = document.createElementNS(SVGNS, "path");
    areaEl.setAttribute("d", area); areaEl.setAttribute("fill", "url(#txArea)");
    areaEl.classList.add("tx-anim");
    svg.appendChild(areaEl);

    var pathEl = document.createElementNS(SVGNS, "path");
    pathEl.setAttribute("d", d); pathEl.setAttribute("fill", "none");
    pathEl.setAttribute("stroke", C.gold); pathEl.setAttribute("stroke-width", "2");
    pathEl.setAttribute("stroke-linejoin", "miter");
    pathEl.classList.add("tx-anim");
    svg.appendChild(pathEl);

    // Live endpoint
    var cx = X(totalUnits), cy = Y(current.price);
    var halo = document.createElementNS(SVGNS, "circle");
    halo.setAttribute("cx", cx); halo.setAttribute("cy", cy); halo.setAttribute("r", "9");
    halo.setAttribute("fill", C.gold); halo.setAttribute("opacity", ".2");
    svg.appendChild(halo);
    var dot = document.createElementNS(SVGNS, "circle");
    dot.setAttribute("cx", cx); dot.setAttribute("cy", cy); dot.setAttribute("r", "4");
    dot.setAttribute("fill", C.gold);
    svg.appendChild(dot);
    if (!m.reduce) {
      halo.innerHTML =
        '<animate attributeName="r" values="6;13;6" dur="2.4s" repeatCount="indefinite"/>' +
        '<animate attributeName="opacity" values=".28;.05;.28" dur="2.4s" repeatCount="indefinite"/>';
    }

    // X-axis labels
    svg.appendChild(text(padL, H - 12, C.muted, "DROP OPEN · " + m.dropStartLabel));
    var ax2 = text(W - padR, H - 12, C.muted, totalUnits + " UNITS SOLD →", "end");
    svg.appendChild(ax2);

    // Crosshair
    var xhair = line(0, padT, 0, H - padB, C.gold, "3 3", ".55");
    xhair.style.display = "none";
    svg.appendChild(xhair);
    var xdot = document.createElementNS(SVGNS, "circle");
    xdot.setAttribute("r", "4.5"); xdot.setAttribute("fill", C.bg);
    xdot.setAttribute("stroke", C.gold); xdot.setAttribute("stroke-width", "2");
    xdot.style.display = "none";
    svg.appendChild(xdot);

    wrap.appendChild(svg);

    // Draw-in animation
    if (!m.reduce) {
      var len = pathEl.getTotalLength();
      pathEl.style.strokeDasharray = len;
      pathEl.style.strokeDashoffset = len;
      pathEl.style.transition = "stroke-dashoffset 1.6s cubic-bezier(.4,0,.2,1)";
      areaEl.style.opacity = "0";
      areaEl.style.transition = "opacity .9s ease 1s";
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          pathEl.style.strokeDashoffset = "0";
          areaEl.style.opacity = "1";
        });
      });
    }

    // Pointer interaction (crosshair + tooltip)
    var bounds = [], bcum = 0;
    segments.forEach(function (s, i) {
      bounds.push({ start: bcum, end: bcum + s.units, price: s.price, tier: i + 1, units: s.units, live: !!s.live });
      bcum += s.units;
    });

    function onMove(evt) {
      var rect = svg.getBoundingClientRect();
      var clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
      var sx = (clientX - rect.left) / rect.width * W;
      var u = Math.max(0, Math.min((sx - padL) / (W - padL - padR) * xMax, totalUnits));
      var seg = null;
      for (var i = 0; i < bounds.length; i++) {
        if (u >= bounds[i].start && u <= bounds[i].end) { seg = bounds[i]; break; }
      }
      if (!seg) seg = bounds[bounds.length - 1];

      var px = Math.max(padL, Math.min(X(u), X(totalUnits)));
      var py = Y(seg.price);
      xhair.setAttribute("x1", px); xhair.setAttribute("x2", px); xhair.style.display = "";
      xdot.setAttribute("cx", px); xdot.setAttribute("cy", py); xdot.style.display = "";

      tip.innerHTML =
        '<div class="tt-price">' + money(Math.round(seg.price * 100)) + "</div>" +
        '<div class="tt-row">Tier <b>' + seg.tier + "</b></div>" +
        '<div class="tt-row">Sold at tier <b>' + seg.units + (seg.live ? " / " + current.capacity : "") + "</b></div>" +
        '<div class="tt-row">Cumulative <b>' + Math.round(Math.min(u, seg.end)) + "</b></div>" +
        (seg.live
          ? '<span class="tt-tag">LIVE TIER</span>'
          : '<span class="tt-tag" style="color:' + C.muted + ';border-color:' + C.grid + '">SOLD OUT</span>');
      tip.style.opacity = "1";
      var wr = wrap.getBoundingClientRect();
      var tipX = (px / W) * wr.width;
      var left = Math.max(8, Math.min(tipX + 14, wr.width - tip.offsetWidth - 8));
      tip.style.left = left + "px";
      tip.style.top = Math.max(8, (py / H) * wr.height - tip.offsetHeight - 14) + "px";
    }
    function onLeave() {
      tip.style.opacity = "0"; xhair.style.display = "none"; xdot.style.display = "none";
    }
    svg.addEventListener("mousemove", onMove);
    svg.addEventListener("mouseleave", onLeave);
    svg.addEventListener("touchstart", onMove, { passive: true });
    svg.addEventListener("touchmove", onMove, { passive: true });
    svg.addEventListener("touchend", onLeave);
  }

  function run() {
    var nodes = document.querySelectorAll("[data-tierly-ticker]");
    for (var i = 0; i < nodes.length; i++) renderOne(nodes[i]);
  }
  window.__tierlyTickerRun = run;
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
  else run();
})();
